import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  TooltipProps,
} from "recharts";
import { CHART_COLORS } from "./chartTokens";
import { InformationHoverIcon } from "./InformationHoverIcon";

export type AutogestionDailyDatum = {
  date: string;
  paqinfoCount: number;
  aiResolvedCount: number;
  enCursoCount: number;
  escaladoCount: number;
  totalConversations: number;
  // null on zero-filled days — a day without conversations has no rate
  autogestionRate: number | null;
};

type Props = {
  title?: string;
  subtitle?: string;
  info?: string;
  colSpan?: number;
  data: AutogestionDailyDatum[];
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload as AutogestionDailyDatum;
  if (row.autogestionRate === null) {
    return (
      <div className="rounded-md border border-border bg-card p-3 shadow-md">
        <p className="text-sm font-semibold text-palomma-primary">{label}</p>
        <p className="text-sm text-muted-foreground">Sin conversaciones</p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-md">
      <p className="text-sm font-semibold text-palomma-primary">{label}</p>
      <p className="text-sm text-palomma-secondary">
        Autogestión: {Math.round(row.autogestionRate)}%
      </p>
      <p className="text-sm text-muted-foreground">
        Gestionado por IA: {row.paqinfoCount + row.aiResolvedCount}
      </p>
      <p className="text-sm text-muted-foreground">En curso: {row.enCursoCount}</p>
      <p className="text-sm text-muted-foreground">Escalado: {row.escaladoCount}</p>
      <p className="text-sm text-muted-foreground">
        Total: {row.totalConversations} conversaciones
      </p>
    </div>
  );
};

const AutogestionDailyChart = ({
  title = "Tasa de autogestión por día",
  subtitle = "Porcentaje de conversaciones autogestionadas cada día",
  info,
  colSpan = 1,
  data,
}: Props) => {
  // Rows are zero-filled server-side, so emptiness means "no day had conversations".
  const hasData = data.some((row) => row.totalConversations > 0);

  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
            {info && <InformationHoverIcon information={info} />}
          </div>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        {!hasData ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            Sin datos en este período
          </div>
        ) : (
          <div className="h-[260px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                  tickMargin={10}
                  axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                  axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
                  allowDecimals={false}
                  tickFormatter={(value: number) => `${value}%`}
                  width={44}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  connectNulls
                  dataKey="autogestionRate"
                  stroke={CHART_COLORS.primary}
                  strokeWidth={2}
                  dot={{ r: 3, fill: CHART_COLORS.primary }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutogestionDailyChart;
